"use client";

import {
  JSX,
  KeyboardEvent,
  MouseEvent,
  PointerEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ChevronDown, ChevronUp, ChevronsUpDown, Filter, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { useHoverSelect } from "@/lib/hooks/use-hover-select";
import { emitModuleInteraction } from "@/lib/module-interaction";
import { useViewerLanguage } from "@/lib/i18n";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import {
  DATA_TABLE_MAX_COLUMNS,
  type ModuleDataTableColumn,
  type ModuleDataTableData,
  type ModuleDataTableRow,
} from "@/types/modules";
import { importsTokenScope } from "./imports-token-scope";
import { chartSlot, formatModuleValue } from "./shared/value-format";
import { ModulePager, pageCountOf } from "./shared/module-pager";
import {
  DATA_TABLE_ROWS_PER_PAGE,
  DATA_TABLE_SEARCH_TRACK_DELAY_MS,
} from "@/config/layout";

const NUMERIC_FORMATS = new Set(["number", "eur", "percent"]);

const PRIMARY_COLUMN_MIN_REM = 10;
const TEXT_COLUMN_MIN_REM = 7;
const NUMERIC_COLUMN_MIN_REM = 6;

const DETAIL_TRANSITION = { duration: 0.28, ease: [0.4, 0, 0.2, 1] as const };

const STICKY_CELL =
  "sticky left-0 z-10 shadow-[inset_-1px_0_0_var(--border)]";
const STICKY_BG = "bg-[var(--card)]";
const STICKY_BG_TRACK = "[background:linear-gradient(var(--track),var(--track)),var(--card)]";

type SortDirection = "asc" | "desc";

type SortState = { col: number; dir: SortDirection } | null;

type IndexedRow = { row: ModuleDataTableRow; index: number };

type ShownColumn = { col: ModuleDataTableColumn; c: number };

function isNumericColumn(col: ModuleDataTableColumn): boolean {
  return NUMERIC_FORMATS.has(col.format ?? "text");
}

function alignOf(col: ModuleDataTableColumn): "left" | "right" {
  return col.align ?? (isNumericColumn(col) ? "right" : "left");
}

function columnMinRem(col: ModuleDataTableColumn, index: number): number {
  if (index === 0) return PRIMARY_COLUMN_MIN_REM;
  return isNumericColumn(col) ? NUMERIC_COLUMN_MIN_REM : TEXT_COLUMN_MIN_REM;
}

function isEmptyCell(value: string | number | undefined): boolean {
  return value === undefined || value === "";
}

function compareCells(
  a: string | number | undefined,
  b: string | number | undefined,
  dir: SortDirection,
  collator: Intl.Collator,
): number {
  const emptyA = isEmptyCell(a);
  const emptyB = isEmptyCell(b);
  if (emptyA || emptyB) return emptyA === emptyB ? 0 : emptyA ? 1 : -1;
  const sign = dir === "asc" ? 1 : -1;
  if (typeof a === "number" && typeof b === "number") return (a - b) * sign;
  return collator.compare(stripInlineMarkdown(String(a)), stripInlineMarkdown(String(b))) * sign;
}

function nextSort(current: SortState, col: number, numeric: boolean): SortState {
  const first: SortDirection = numeric ? "desc" : "asc";
  if (!current || current.col !== col) return { col, dir: first };
  if (current.dir === first) return { col, dir: first === "asc" ? "desc" : "asc" };
  return null;
}

export default function ModuleDataTable({
  data,
  grayscale,
  accent,
  className,
}: {
  data: ModuleDataTableData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t, intlLocale } = useViewerLanguage();
  const collator = useMemo(
    () => new Intl.Collator(intlLocale, { numeric: true, sensitivity: "base" }),
    [intlLocale],
  );
  const columns = useMemo(
    () => (data?.columns ?? []).slice(0, DATA_TABLE_MAX_COLUMNS),
    [data?.columns],
  );
  const rows = useMemo(() => data?.rows ?? [], [data?.rows]);

  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortState>(null);
  const [page, setPage] = useState(0);
  const [visibility, setVisibility] = useState<Record<number, boolean>>({});
  const [menuOpen, setMenuOpen] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);

  const searchColumns = useMemo(
    () => columns.map((col, c) => (isNumericColumn(col) ? -1 : c)).filter((c) => c >= 0),
    [columns],
  );

  const tagColors = useMemo(() => {
    const maps = columns.map(() => new Map<string, string>());
    columns.forEach((col, c) => {
      if (col.format !== "tag") return;
      for (const row of rows) {
        const key = String(row.cells[c] ?? "");
        if (key && !maps[c].has(key)) maps[c].set(key, chartSlot(maps[c].size));
      }
    });
    return maps;
  }, [columns, rows]);

  const searchIndex = useMemo(
    () =>
      rows.map((row) =>
        searchColumns
          .map((c) => stripInlineMarkdown(String(row.cells[c] ?? "")).toLowerCase())
          .join("\u0000"),
      ),
    [rows, searchColumns],
  );

  const filtered = useMemo<IndexedRow[]>(() => {
    const q = query.trim().toLowerCase();
    const list = rows
      .map((row, index) => ({ row, index }))
      .filter(({ index }) => !q || searchIndex[index].includes(q));
    if (!sort) return list;
    return [...list].sort(
      (a, b) =>
        compareCells(a.row.cells[sort.col], b.row.cells[sort.col], sort.dir, collator) ||
        a.index - b.index,
    );
  }, [rows, searchIndex, query, sort, collator]);

  const pageSize = DATA_TABLE_ROWS_PER_PAGE;
  const paged = rows.length > pageSize;
  const detailSlots = columns.length;
  const slots = Math.max(1, Math.min(pageSize, Math.max(rows.length, detailSlots)));
  const pages = pageCountOf(filtered.length, pageSize);
  const current = Math.min(page, pages - 1);
  const pageRows = filtered.slice(current * pageSize, current * pageSize + pageSize);

  const isVisible = (c: number): boolean => c === 0 || visibility[c] !== false;

  const shown: ShownColumn[] = columns
    .map((col, c) => ({ col, c }))
    .filter(({ c }) => isVisible(c));

  const tableMinWidth = `${shown.reduce((sum, { col, c }) => sum + columnMinRem(col, c), 0)}rem`;

  const lastTrackedQuery = useRef("");
  useEffect(() => {
    const q = query.trim();
    if (q === lastTrackedQuery.current) return;
    const timer = window.setTimeout(() => {
      lastTrackedQuery.current = q;
      emitModuleInteraction(inputRef.current, "search", { kind: "control", id: "search" }, {
        queryLength: q.length,
      });
    }, DATA_TABLE_SEARCH_TRACK_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [query]);

  const hasHover = useHoverCapable();
  const reduceMotion = useReducedMotion() ?? false;
  const cursor = useHoverSelect<number>();
  const { setHovered } = cursor;
  const activeRow = cursor.effective;
  const activeShown = activeRow !== null && pageRows.some((r) => r.index === activeRow);

  const detailIndex = cursor.selected;
  const detailRow = detailIndex !== null ? rows[detailIndex] : undefined;
  const detailOpen = detailRow !== undefined;

  const overlayRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const restoreFocusRef = useRef(false);

  const closeDetail = (element: Element | null) => {
    if (detailIndex === null) return;
    restoreFocusRef.current = Boolean(
      overlayRef.current && overlayRef.current.contains(document.activeElement),
    );
    cursor.select(detailIndex, {
      kind: "row",
      id: rows[detailIndex]?.id ?? detailIndex,
      index: detailIndex,
      element,
    });
  };

  const closeDetailRef = useRef(closeDetail);
  closeDetailRef.current = closeDetail;

  useEffect(() => {
    if (!detailOpen) return;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") closeDetailRef.current(overlayRef.current);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [detailOpen]);

  useEffect(() => {
    if (detailOpen) {
      closeButtonRef.current?.focus({ preventScroll: true });
      return;
    }
    if (!restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    const opener = openerRef.current;
    if (opener?.isConnected) opener.focus({ preventScroll: true });
  }, [detailOpen]);

  const openDetail = (row: ModuleDataTableRow, r: number, element: HTMLElement) => {
    if (cursor.selected === r) return;
    openerRef.current = element;
    cursor.select(r, { kind: "row", id: row.id ?? r, index: r, element });
  };

  const onRowKeyDown = (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    e.currentTarget.click();
  };

  const toggleSort = (col: ModuleDataTableColumn, c: number, e: MouseEvent<HTMLButtonElement>) => {
    const next = nextSort(sort, c, isNumericColumn(col));
    emitModuleInteraction(e.currentTarget, "sort", { kind: "control", id: col.id ?? null, index: c }, {
      direction: next?.dir ?? "none",
    });
    setSort(next);
    setPage(0);
  };

  const toggleColumn = (col: ModuleDataTableColumn, c: number, visible: boolean) => {
    if (c === 0) return;
    emitModuleInteraction(
      menuTriggerRef.current,
      "column_toggle",
      { kind: "control", id: col.id ?? null, index: c },
      { visible },
    );
    setVisibility((prev) => ({ ...prev, [c]: visible }));
  };

  const menuOpenAtPointerDown = useRef(false);

  const onMenuPointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    menuOpenAtPointerDown.current = menuOpen;
    e.preventDefault();
  };

  const onMenuClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (e.detail > 0) setMenuOpen(!menuOpenAtPointerDown.current);
  };

  const renderCell = (
    col: ModuleDataTableColumn,
    c: number,
    raw: string | number | undefined,
    wrap = false,
  ): ReactNode => {
    if (isEmptyCell(raw)) return <span className="text-[var(--subtle)]">–</span>;
    if (col.format === "tag") {
      const text = stripInlineMarkdown(String(raw));
      return (
        <span
          className={cn(
            "min-w-0 items-center gap-1.5",
            wrap ? "inline-flex max-w-full" : "flex",
          )}
        >
          <span
            aria-hidden
            className="size-1.5 shrink-0 rounded-full"
            style={{ background: tagColors[c]?.get(String(raw)) ?? "var(--chart-1)" }}
          />
          <span className={wrap ? "min-w-0 break-words" : "truncate"}>{text}</span>
        </span>
      );
    }
    if (typeof raw === "number") {
      const format =
        col.format === "eur" || col.format === "percent" ? col.format : "number";
      return formatModuleValue(raw, { format, digits: col.digits, locale: intlLocale });
    }
    return stripInlineMarkdown(String(raw));
  };

  const rowBorder = (slot: number) =>
    slot < slots - 1 ? "border-b border-dashed" : "border-b-0";

  const fillerCount = Math.max(0, slots - Math.max(pageRows.length, filtered.length === 0 ? 1 : 0));

  const detailHeading = detailRow
    ? stripInlineMarkdown(String(detailRow.cells[0] ?? "")) || t("Row details")
    : "";

  return (
    <div
      className={cn("@container flex w-full min-w-0 flex-col gap-3", className)}
      style={importsTokenScope(grayscale, accent)}
    >
      <div className="flex h-8 min-w-0 items-center gap-2">
        {searchColumns.length > 0 && (
          <div className="relative min-w-0 flex-1">
            <Search
              aria-hidden
              className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-[var(--subtle)]"
            />
            <Input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0);
              }}
              placeholder={t("Search")}
              aria-label={t("Search table")}
              autoComplete="off"
              className="h-8 border-[var(--border)] bg-transparent pl-8 text-[var(--fg)] placeholder:text-[var(--subtle)] focus-visible:border-[var(--border-strong)] dark:bg-transparent [&::-webkit-search-cancel-button]:hidden"
            />
          </div>
        )}
        <DropdownMenu modal={false} open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              ref={menuTriggerRef}
              type="button"
              variant="ghost"
              size="sm"
              onPointerDown={onMenuPointerDown}
              onClick={onMenuClick}
              className={cn(
                "shrink-0 border border-[var(--border)] text-[var(--muted)] hover:bg-[var(--track)] hover:text-[var(--fg)] dark:hover:bg-[var(--track)]",
                searchColumns.length === 0 && "ml-auto",
              )}
            >
              <Filter />
              {t("Filter")}
              <span className="tabular-nums text-[var(--subtle)]">
                {shown.length}/{columns.length}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {columns.map((col, c) => (
              <DropdownMenuCheckboxItem
                key={col.id ?? c}
                checked={isVisible(c)}
                disabled={c === 0}
                onCheckedChange={(checked) => toggleColumn(col, c, checked === true)}
                onSelect={(e) => e.preventDefault()}
              >
                <span className="truncate">{stripInlineMarkdown(col.label)}</span>
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="relative flex min-w-0 flex-col">
        <div inert={detailOpen} className="flex min-w-0 flex-col gap-3">
          <div className="overflow-hidden rounded-xl border border-[var(--border)]">
            <div className="w-full overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <table
                className="w-full table-fixed border-collapse text-sm"
                style={{ minWidth: tableMinWidth }}
              >
                <thead>
                  <tr className="h-9 border-b border-[var(--border)] bg-[var(--track)]">
                    {shown.map(({ col, c }) => {
                      const active = sort?.col === c;
                      const right = alignOf(col) === "right";
                      const SortIcon = !active ? ChevronsUpDown : sort?.dir === "asc" ? ChevronUp : ChevronDown;
                      return (
                        <th
                          key={col.id ?? c}
                          scope="col"
                          aria-sort={active ? (sort?.dir === "asc" ? "ascending" : "descending") : "none"}
                          className={cn(
                            "overflow-hidden px-3 py-0 text-xs font-normal text-[var(--subtle)]",
                            right ? "text-right" : "text-left",
                            c === 0 && cn(STICKY_CELL, STICKY_BG_TRACK),
                          )}
                          style={{ width: `${columnMinRem(col, c)}rem` }}
                        >
                          <button
                            type="button"
                            onClick={(e) => toggleSort(col, c, e)}
                            className={cn(
                              "inline-flex max-w-full cursor-pointer items-center gap-1 rounded-sm align-middle outline-none hover:text-[var(--fg)] focus-visible:ring-1 focus-visible:ring-[var(--border-strong)]",
                              right && "flex-row-reverse",
                              active && "text-[var(--fg)]",
                            )}
                          >
                            <span className="min-w-0 truncate">{stripInlineMarkdown(col.label)}</span>
                            <SortIcon
                              aria-hidden
                              className={cn("size-3 shrink-0", active ? "opacity-100" : "opacity-40")}
                            />
                          </button>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map(({ row, index: r }, slot) => {
                    const isActive = activeRow === r;
                    const dim = activeShown && !isActive;
                    return (
                      <tr
                        key={row.id ?? r}
                        tabIndex={0}
                        aria-selected={cursor.selected === r}
                        aria-haspopup="dialog"
                        className={cn(
                          "h-10 cursor-pointer border-[var(--border-dashed)] transition-[opacity,background-color] duration-150 outline-none focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-[var(--border-strong)]",
                          rowBorder(slot),
                          isActive && "bg-[var(--track)]",
                          dim && "opacity-60",
                        )}
                        onMouseEnter={hasHover ? () => setHovered(r) : undefined}
                        onMouseLeave={hasHover ? () => setHovered(null) : undefined}
                        onKeyDown={onRowKeyDown}
                        onClick={(e) => openDetail(row, r, e.currentTarget)}
                      >
                        {shown.map(({ col, c }) => (
                          <td
                            key={col.id ?? c}
                            className={cn(
                              "truncate px-3 py-0",
                              alignOf(col) === "right" ? "text-right tabular-nums" : "text-left",
                              c === 0 ? "font-medium text-[var(--fg)]" : "text-[var(--muted)]",
                              isNumericColumn(col) && c !== 0 && "text-xs",
                              c === 0 && cn(STICKY_CELL, isActive ? STICKY_BG_TRACK : STICKY_BG),
                            )}
                          >
                            {renderCell(col, c, row.cells[c])}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr className={cn("h-10 border-[var(--border-dashed)]", rowBorder(0))}>
                      <td
                        colSpan={shown.length}
                        className="truncate px-3 py-0 text-left text-[var(--subtle)]"
                      >
                        {t("No results")}
                      </td>
                    </tr>
                  )}
                  {Array.from({ length: fillerCount }).map((_, i) => (
                    <tr
                      key={`filler-${i}`}
                      aria-hidden
                      className={cn(
                        "h-10 border-transparent",
                        rowBorder(slots - fillerCount + i),
                      )}
                    >
                      <td colSpan={shown.length} className="p-0" />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {paged && (
            <ModulePager
              page={current}
              pageSize={pageSize}
              total={filtered.length}
              onPageChange={setPage}
            />
          )}
        </div>

        <AnimatePresence>
          {detailRow && (
            <motion.div
              key={detailIndex}
              ref={overlayRef}
              role="dialog"
              aria-label={detailHeading}
              initial={{ opacity: 0, filter: "blur(6px)" }}
              animate={{ opacity: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, filter: "blur(6px)" }}
              transition={reduceMotion ? { duration: 0 } : DETAIL_TRANSITION}
              className="absolute inset-0 z-20 flex min-w-0 flex-col overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--card)]"
            >
              <div className="flex shrink-0 items-start gap-2 border-b border-[var(--border)] py-2 pr-2 pl-3">
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate text-xs text-[var(--subtle)]">
                    {stripInlineMarkdown(columns[0]?.label ?? "")}
                  </span>
                  <h3 className="truncate text-sm font-medium text-[var(--fg)]">{detailHeading}</h3>
                </div>
                <Button
                  ref={closeButtonRef}
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t("Close details")}
                  onClick={(e) => closeDetail(e.currentTarget)}
                  className="shrink-0 text-[var(--muted)] hover:bg-[var(--track)] hover:text-[var(--fg)] dark:hover:bg-[var(--track)]"
                >
                  <X className="size-3.5" />
                </Button>
              </div>
              <dl className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-3">
                {columns.slice(1).map((col, i) => {
                  const c = i + 1;
                  return (
                    <div
                      key={col.id ?? c}
                      className="flex min-h-10 items-baseline justify-between gap-4 border-b border-dashed border-[var(--border-dashed)] py-2.5 last:border-b-0"
                    >
                      <dt className="max-w-[45%] shrink-0 break-words text-xs text-[var(--subtle)]">
                        {stripInlineMarkdown(col.label)}
                      </dt>
                      <dd className="min-w-0 break-words text-right text-sm text-[var(--fg)] tabular-nums">
                        {renderCell(col, c, detailRow.cells[c], true)}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
